import React, {useState, useEffect, useRef} from "react";
import {LookDataType, LookItemType} from "./LookTypes";
import MobilePopup from "./MobilePopup";
import MobileTitleBlock from "./MobileTitleBlock";
import LookImageSection from "./LookImageSection";
import LookInfoSection from "./LookInfoSection";
import isSameObject from "../../../helpers/isSameObject";
import LookProductsSlider from "./LookProductsSlider";
import TagRelatedLooksSliders from "./TagRelatedLooksSliders";
import {useMediaQuery} from "react-responsive";
import {queryBreakpointSmall} from "./stlHelper";
// TODO
// tags/creator links: should lead to an overview page

const LookDetail: React.FC<{
  look: LookDataType;
  lookItems: LookItemType[];
  isLookDetailPageItem: boolean;
  setLook?: React.Dispatch<React.SetStateAction<LookDataType | null>>;
}> = ({look, lookItems, isLookDetailPageItem, setLook}) => {
  const [activeProduct, setActiveProduct] = useState<LookItemType | null>(null);

  const isMobile = useMediaQuery({query: queryBreakpointSmall});
  const [isClosing, setIsClosing] = useState(false);

  const imageRef = useRef<HTMLDivElement>(null);
  const mobilePopupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => {
      const imageElement = imageRef.current;
      const popupElement = mobilePopupRef.current;

      if (!imageElement || !popupElement) {
        return;
      }

      const lookImageHeight = imageElement.getBoundingClientRect().height;
      const viewportHeight = window.innerHeight;

      popupElement.style.top = `${lookImageHeight}px`;
      popupElement.style.height = `${viewportHeight - lookImageHeight}px`;
    };

    if (isMobile && activeProduct) {
      // Initial einmal aufrufen
      handleResize();

      window.addEventListener("resize", handleResize);
    }

    return () => window.removeEventListener("resize", handleResize);
  }, [isMobile, activeProduct]);

  // Mobiles Popup schließen, wenn Look nicht mehr im Viewport
  useEffect(() => {
    const imageElement = imageRef.current;

    if (!(isMobile && activeProduct && imageElement)) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        // Wenn das Bild nicht mehr sichtbar ist
        if (!entries[0].isIntersecting) {
          handleClose();
        }
      },
      {
        // Root ist null = viewport
        root: null,
        // Schwellenwert bei 0.75, sobald das Element zu 34% aus dem Viewport ist
        threshold: 0.66,
        // Optional: Zusätzlicher Spielraum, in dem das Element noch als "sichtbar" gilt
        rootMargin: "0px",
      },
    );

    observer.observe(imageElement);

    // Cleanup
    return () => observer.unobserve(imageElement);
  }, [isMobile, activeProduct]);

  const handleClose = () => {
    setIsClosing(true);
    // Warte auf das Ende der Animation bevor der State zurückgesetzt wird
    setTimeout(() => {
      setActiveProduct(null);
      setIsClosing(false);
    }, 200); // Dauer der Animation
  };

  const handleDotClick = async (product: LookItemType, event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();

    if (isSameObject<LookItemType>(activeProduct, product)) {
      setActiveProduct(null);
    } else if (!isMobile) {
      setActiveProduct(product);
    } else {
      const imageElement = imageRef.current;
      if (!imageElement) {
        return;
      }

      // Position des Bilds relativ zum Viewport-Top
      const imageRect = imageElement.getBoundingClientRect();

      const scrollPosition = window.scrollY + imageRect.top;

      // Zum Bild scrollen
      window.scrollTo({
        top: scrollPosition,
        behavior: "smooth",
      });

      // Produkt sofort aktivieren
      setActiveProduct(product);

      // Minimale Verzögerung für smoother Scroll
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Popup positionieren
      const popupElement = mobilePopupRef.current;
      if (popupElement) {
        const viewportHeight = window.innerHeight;
        const lookImageHeight = imageRect.height;

        // Fixed Position vom Viewport-Top
        popupElement.style.top = `${lookImageHeight}px`;
        popupElement.style.height = `${viewportHeight - lookImageHeight}px`;
      }
    }
  };

  return (
    <>
      {/* eslint-disable-next-line no-underscore-dangle */}
      <div className="stl-container" id={`look-id-${look._id}`}>
        {isLookDetailPageItem && isMobile && <MobileTitleBlock look={look} />}

        <div className="stl-detail-item grid">
          <LookImageSection
            {...{look, lookItems, activeProduct, handleClose, handleDotClick, isMobile, isLookDetailPageItem}}
            ref={imageRef}
          />
          <LookInfoSection look={look} isLookDetailPageItem={isLookDetailPageItem} />
        </div>

        {isLookDetailPageItem && isMobile && activeProduct && (
          <MobilePopup {...{activeProduct, handleClose, isClosing}} ref={mobilePopupRef} />
        )}
        {isLookDetailPageItem && setLook && (
          <>
            <LookProductsSlider lookItems={lookItems} />
            <TagRelatedLooksSliders currentLook={look} setLook={setLook} />
          </>
        )}
      </div>
    </>
  );
};

export default LookDetail;
